// TeamProvider — React context for the currently-selected team across the
// authenticated dashboard. The team ID is derived from the URL (when on a
// /teams/:teamId/... route) or from sessionStorage (last-selected team).
//
// Wire-up V1 is intentionally minimal: provides current team ID + a setter.
// Full team membership data + role lookups will be layered in by future
// dashboard work (#12).

import { createContext, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

const TeamContext = createContext({ teamId: null, setTeamId: () => {} })

const STORAGE_KEY = 'cairn:lastTeamId'

export function TeamProvider({ children }) {
  const routeParams = useParams()
  const [teamId, setTeamIdState] = useState(() => {
    if (typeof sessionStorage === 'undefined') return null
    return sessionStorage.getItem(STORAGE_KEY)
  })

  // When the URL contains a teamId, sync it into context + sessionStorage.
  useEffect(() => {
    if (routeParams.teamId && routeParams.teamId !== teamId) {
      setTeamIdState(routeParams.teamId)
      try {
        sessionStorage.setItem(STORAGE_KEY, routeParams.teamId)
      } catch {
        // ignore — sessionStorage unavailable (e.g., SSR)
      }
    }
  }, [routeParams.teamId, teamId])

  const setTeamId = (id) => {
    setTeamIdState(id)
    try {
      if (id) sessionStorage.setItem(STORAGE_KEY, id)
      else sessionStorage.removeItem(STORAGE_KEY)
    } catch {
      // ignore
    }
  }

  return <TeamContext.Provider value={{ teamId, setTeamId }}>{children}</TeamContext.Provider>
}

export function useTeam() {
  return useContext(TeamContext)
}
